import { Material, MaterialFolder, MaterialType } from 'api';
import Typography from 'helpers/fonts';
import React, { useEffect, useState } from 'react';
import { HEADER_HEIGHT } from 'helpers/constants';
import { useSmallScreen } from 'helpers/isSmallScreen';
import TabPaneFilter from '../tabpanefilter';
import TabPaneSquares from './tabpane-squares';
import ColorPalette from './color-palette';
import { useUiState } from 'state/ui-state/ui-state';
import { Radio, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAppState, UserStateMaterials } from 'state/appstate/appstate';
import { useNotificationStore } from '../../state/notificationstate/notificationstate';
import { drawsEveryRoofingAsEpdm, EPDM_FOLDER_ID } from './roofing-preview';

export interface PaintColor {
  siding: string | null;
  trim: string | null;
}

export const COLORS = [
  '#262151',
  '#3C3E8D',
  '#0665B0',
  '#AED0E7',
  '#4D6D62',
  '#2E5123',
  '#1E321B',
  '#FDF6BB',
  '#E0BE90',
  '#F9EE6E',
  '#981619',
  '#5B3A29',
  '#2D2422',
  '#FAFBFB',
  '#C6C6C7',
  '#545561',
  '#383940',
  '#1B1B1A'
];

export interface TabPaneProps {
  materialType: MaterialType;
  filters?: Array<MaterialFolder>;
  description: string;
  options?: Array<Material>;
  optionSettings?: Array<any>;
  runningTotalHeight: number;
}

export enum MaterialOrientation {
  HORIZONTAL,
  VERTICAL ,
}

export const CEDAR_MATERIAL_ID = '5';

// The API drops option folders left empty by the style's unavailable options,
// but materials are narrowed in the browser, so their folders are narrowed
// here, against the materials this pane shows, or the customer is offered a
// folder with nothing in it.
const foldersHoldingMaterials = (
  folders: Array<MaterialFolder>,
  materials: Array<Material>
): Array<MaterialFolder> => {
  const occupiedFolderIds = new Set(
    materials.map(({ materialFolderId }) => materialFolderId)
  );
  return folders.filter(({ id }) => occupiedFolderIds.has(id));
};

const TabPane: React.FunctionComponent<TabPaneProps> = ({
  materialType,
  filters,
  options,
  runningTotalHeight,
}) => {
  const { t } = useTranslation();
  const { setNotification, messages } = useNotificationStore();
  const { selectedColor, setSelectedColor } = useUiState();
  const { uState, setSiding, styleProduct } = useAppState();
  const siding = uState!.materials.siding;
  const trim = uState!.materials.trim;
  // Looked up among the materials this pane offers, which a style may have
  // narrowed to one direction, so the orientation toggle only appears for a
  // siding that is actually offered both ways.
  const activeSidingMaterial = siding
    ? options?.find((m) => siding === m.id || siding === m.verticalSidingId)
    : undefined;
  const activeTrimMaterial = trim
    ? styleProduct.product?.materials?.find((m) => trim === m.id)
    : undefined;
  const verticalOrientation = siding === activeSidingMaterial?.verticalSidingId;
  const [orientation, setOrientation] = useState<MaterialOrientation>(verticalOrientation
    ? MaterialOrientation.VERTICAL
    : MaterialOrientation.HORIZONTAL
  );

  const activeMaterial = styleProduct.product?.materials?.find(
    (m) =>
      uState!.materials[materialType.id as keyof UserStateMaterials] === m.id ||
      (m.verticalSidingId !== undefined &&
        uState!.materials[materialType.id as keyof UserStateMaterials] ===
        m.verticalSidingId)
  )
  const shownFilters = foldersHoldingMaterials(filters ?? [], options ?? []);
  const [selectedFilter, setSelectedFilter] = useState<
    MaterialFolder | undefined
  >(shownFilters.find(({ id }) => id === activeMaterial?.materialFolderId));
  const [filteredOptions, setFilteredOptions] = useState<Array<Material>>([]);
  const [showedMessages, setShowedMessages] = useState<Record<string, boolean>>(
    {}
  );

  // update filteredOptions on change of the filter
  useEffect(() => {
    if (!options) {
      return;
    }
    setFilteredOptions(
      selectedFilter
        ? options?.filter((o) => o.materialFolderId === selectedFilter.id)
        : options
    );
  }, [selectedFilter, options]);

  const isSmallScreen = useSmallScreen();
  const maxHeight = isSmallScreen
    ? 'auto'
    : HEADER_HEIGHT + (runningTotalHeight || 0);

  useEffect(() => {
    const material = options?.find(m => uState!.materials.siding === m.id
      || uState!.materials.siding === m.verticalSidingId!
    );
    if(material) {
      setSiding(orientation === MaterialOrientation.VERTICAL
        ? material.verticalSidingId!
        : material.id)
    }
  }, [orientation])

  const Footer = <div style={{ gridArea: 'footer' }}>
    {materialType.id === 'siding' && activeSidingMaterial?.verticalSidingId && (
      <div style={{ marginTop: 8 }}>
        <div>
          <Typography styleid={9}>{t('materialOrientation.title')}</Typography>
        </div>
        <Radio.Group
          value={orientation}
          onChange={(e) => setOrientation(e.target.value)}
        >
          <Space size={[8, 8]} wrap>
            <Radio.Button value={MaterialOrientation.HORIZONTAL}>
              {t('materialOrientation.horizontal')}
            </Radio.Button>
            <Radio.Button value={MaterialOrientation.VERTICAL}>
              {t('materialOrientation.vertical')}
            </Radio.Button>
          </Space>
        </Radio.Group>
      </div>
    )}
    {((materialType.id === 'siding' &&
          activeSidingMaterial?.materialFolderId === CEDAR_MATERIAL_ID) ||
        (materialType.id === 'trim' &&
          activeTrimMaterial?.materialFolderId === CEDAR_MATERIAL_ID)) &&
      selectedFilter?.id === CEDAR_MATERIAL_ID && (
        <ColorPalette
          selectedColor={selectedColor[materialType.id]}
          colors={COLORS}
          onSelectColor={(color: string | null) =>
            setSelectedColor({ ...selectedColor, [materialType.id]: color })
          }
          description={t('materialsSidebar.colorPaletteDescription', {
            type: materialType.id,
          })}
        />
      )}
  </div>

  return (
    <div style={{ display: 'flex', height: isSmallScreen ? 'calc(100% - 22px)' : '100%' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: 'auto 1fr auto', // calculate the rows according
          gridTemplateAreas: `'filter'
            'main'
            'footer'`,
          background: Theme['@grey-lightest'],
          padding: '16px',
          flex: '1 1 auto',
          maxHeight: `calc(${document.documentElement.style.getPropertyValue('--app-height')} - ${maxHeight}px)`,
        }}
      >
        <div style={{ gridArea: 'filter' }}>
          {shownFilters.length > 1 && (
            <TabPaneFilter
              filters={shownFilters}
              selectFilter={(filter) => {
                const warning = 'notifications.app.warning.roofShingles';
                if (
                  materialType?.id === 'roofing' &&
                  filter?.id != selectedFilter?.id &&
                  !messages.some(({ message }) => warning === message) &&
                  (filter?.id === '5' || filter?.id === '20') && // Cedar, Asphalt
                  filter?.id &&
                  !showedMessages[filter.id]
                ) {
                  setNotification(
                    warning,
                    'warning',
                    'small',
                    false,
                    undefined,
                    'left'
                  );
                  setShowedMessages((value) => ({
                    ...value,
                    [filter?.id]: true,
                  }));
                }
                const roofingPreviewNote =
                  'notifications.app.warning.roofingPreviewAsEpdm';
                if (
                  materialType?.id === 'roofing' &&
                  filter?.id &&
                  filter.id !== selectedFilter?.id &&
                  filter.id !== EPDM_FOLDER_ID &&
                  drawsEveryRoofingAsEpdm(styleProduct.style) &&
                  !messages.some(({ message }) => message === roofingPreviewNote)
                ) {
                  setNotification(
                    roofingPreviewNote,
                    'warning',
                    'small',
                    false,
                    undefined,
                    'left'
                  );
                }
                setSelectedFilter(filter);
              }}
              selectedFilter={selectedFilter}
            />
          )}
        </div>
        <div
          style={{ gridArea: 'main', flex: '1 1 auto' }}
          className="visible-scroll-green"
        >
          <div style={{ margin: '16px 0' }}>
            <div>
              <Typography styleid={10}>{selectedFilter?.title} {materialType.displayName}</Typography>
            </div>
            <Typography styleid={9}>{selectedFilter?.description}</Typography>
          </div>
          <TabPaneSquares
            options={filteredOptions}
            position={materialType.id as any}
            orientation={orientation}
            changeOrientation={setOrientation}
            materialFolder={selectedFilter?.id}
          />
          {!isSmallScreen ? <></> : Footer}
        </div>
        {isSmallScreen ? <></> : Footer}
      </div>
    </div>
  );
};

export default TabPane;
